import { c as _c } from "react/compiler-runtime";
import { GetStaticPaths, GetStaticProps } from "next";
import { useRouter } from "next/router";
import { isSupportedLocale, normalizeLocale, t } from "@/lib/i18n";
import { NotFound } from "@/components/NotFound";
import type { ProductCategory, MilitaryFaction, MilitarySubcategory } from "@/data/products";
import { products, isMilitaryFaction, parseBrandsParam } from "@/data/products";
import { isProductsSlugValid, isProductSlugPattern, getAllProductsUrl, parseProductIdFromSlug, getProductSlug } from "@/lib/urls";
import { ProductsListContent } from "@/components/ProductsListContent";
import { ProductDetailContent } from "@/components/ProductDetailContent";
import { LOCALES } from "@/lib/i18n";
function isValidCategory(v: unknown): v is ProductCategory {
  return v === "military" || v === "civilian";
}
function isValidSubcategory(v: unknown): v is MilitarySubcategory {
  return v === "tanks_vehicles" || v === "ships" || v === "other";
}
export const getStaticPaths: GetStaticPaths = async () => {
  const paths: {
    params: {
      locale: string;
      productsSlug: string;
    };
  }[] = [];
  // Uwaga: NIE generujemy tutaj paths dla list slugów (np. `(en, sets)`,
  // `(de, sets)`, `(pl, zestawy)`, `(it, set)`, `(ru, nabory)` itd.).
  // Te trasy obsługują konkretne pliki: pages/sets.tsx, pages/de/sets.tsx,
  // pages/pl/zestawy.tsx, pages/it/set.tsx, pages/ru/nabory.tsx, …
  // Pre-renderowanie ich również tutaj powoduje konflikt:
  //   "Conflicting paths returned from getStaticPaths" przy `next build`.
  // Lista jest obsługiwana on-demand przez `fallback: "blocking"` tylko jako
  // safety-net dla starych URL-i — normalnie ruch idzie do plików concrete.
  for (const locale of LOCALES) {
    for (const p of products) {
      paths.push({
        params: {
          locale,
          productsSlug: getProductSlug(p)
        }
      });
    }
  }
  return {
    paths,
    fallback: "blocking"
  };
};
export const getStaticProps: GetStaticProps = async ctx => {
  const localeParam = ctx.params?.locale as string || "";
  const productsSlug = ctx.params?.productsSlug as string || "";
  if (!isSupportedLocale(localeParam) || !productsSlug) {
    return {
      notFound: true
    };
  }
  const locale = localeParam;

  // Soft-duplicate product URLs (e.g. after a rename): always 301 to the current slug.
  if (isProductSlugPattern(productsSlug)) {
    const productId = parseProductIdFromSlug(productsSlug);
    const product = products.find(p => p.id === productId);
    if (!product) {
      return {
        notFound: true
      };
    }
    const canonicalSlug = getProductSlug(product);
    if (productsSlug !== canonicalSlug) {
      return {
        redirect: {
          destination: `/${locale}/${canonicalSlug}`,
          permanent: true
        }
      };
    }
    return {
      props: {}
    };
  }
  if (isProductsSlugValid(productsSlug, locale)) {
    return {
      props: {}
    };
  }

  // Truncated / broken product slugs from crawlers (e.g. /en/pzkpfw-iv-ausf-).
  const needle = productsSlug.replace(/-+$/g, "").toLowerCase();
  if (needle.length >= 8) {
    const matches = products.filter(p => {
      const slug = getProductSlug(p).toLowerCase();
      return slug === needle || slug.startsWith(`${needle}-`);
    });
    if (matches.length === 1) {
      return {
        redirect: {
          destination: `/${locale}/${getProductSlug(matches[0])}`,
          permanent: true
        }
      };
    }
  }
  return {
    notFound: true
  };
};
export default function ProductsIndexPage() {
  const $ = _c(47);
  const router = useRouter();
  let T0;
  let basePath;
  let brands;
  let category;
  let factions;
  let piecesMax;
  let piecesMin;
  let scales;
  let searchQuery;
  let subcategory;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== router.query) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const locale = normalizeLocale(router.query.locale as string || "en");
      const productsSlug = router.query.productsSlug as string;
      if (!productsSlug) {
        t4 = <NotFound locale={locale} messageKey="notFound.page" />;
        break bb0;
      }
      if (isProductSlugPattern(productsSlug)) {
        let t5;
        if ($[16] !== productsSlug) {
          t5 = parseProductIdFromSlug(productsSlug);
          $[16] = productsSlug;
          $[17] = t5;
        } else {
          t5 = $[17];
        }
        const productId = t5;
        let t6;
        if ($[18] !== productId) {
          t6 = products.find(p => p.id === productId);
          $[18] = productId;
          $[19] = t6;
        } else {
          t6 = $[19];
        }
        const product = t6;
        if (product) {
          t4 = <ProductDetailContent product={product} locale={locale} />;
          break bb0;
        }
        t4 = <NotFound locale={locale} messageKey="notFound.product" />;
        break bb0;
      }
      if (!isProductsSlugValid(productsSlug, locale)) {
        t4 = <NotFound locale={locale} messageKey="notFound.page" />;
        break bb0;
      }
      const query = router.query;
      let t5;
      if ($[20] !== query.q) {
        t5 = typeof query.q === "string" && query.q.trim() ? query.q.trim() : undefined;
        $[20] = query.q;
        $[21] = t5;
      } else {
        t5 = $[21];
      }
      searchQuery = t5;
      let t6;
      if ($[22] !== query.brands) {
        t6 = parseBrandsParam(query.brands);
        $[22] = query.brands;
        $[23] = t6;
      } else {
        t6 = $[23];
      }
      brands = t6;
      let t7;
      if ($[24] !== query.category) {
        t7 = isValidCategory(query.category) ? query.category : undefined;
        $[24] = query.category;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      category = t7;
      let t8;
      if ($[26] !== query.subcategory) {
        t8 = isValidSubcategory(query.subcategory) ? query.subcategory : undefined;
        $[26] = query.subcategory;
        $[27] = t8;
      } else {
        t8 = $[27];
      }
      subcategory = t8;
      const factionsRaw = typeof query.faction === "string" && query.faction ? query.faction : "";
      let t9;
      if ($[28] !== factionsRaw) {
        t9 = factionsRaw.length > 0 ? factionsRaw.split(",").filter(_temp) : undefined;
        $[28] = factionsRaw;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      factions = t9;
      const scalesRaw = typeof query.scale === "string" && query.scale ? query.scale : "";
      let t10;
      if ($[30] !== scalesRaw) {
        t10 = scalesRaw.length > 0 ? scalesRaw.split(",").filter(Boolean) : undefined;
        $[30] = scalesRaw;
        $[31] = t10;
      } else {
        t10 = $[31];
      }
      scales = t10;
      piecesMin = typeof query.piecesMin === "string" && query.piecesMin ? parseInt(query.piecesMin, 10) : undefined;
      piecesMax = typeof query.piecesMax === "string" && query.piecesMax ? parseInt(query.piecesMax, 10) : undefined;
      basePath = getAllProductsUrl(locale);
      T0 = ProductsListContent;
      t0 = locale;
      t1 = t(locale, "home.latestTitle");
      t2 = t(locale, "home.bestsellersSubtitle");
      t3 = t(locale, "home.latestTitle");
    }
    $[0] = router.query;
    $[1] = T0;
    $[2] = basePath;
    $[3] = brands;
    $[4] = category;
    $[5] = factions;
    $[6] = piecesMax;
    $[7] = piecesMin;
    $[8] = scales;
    $[9] = searchQuery;
    $[10] = subcategory;
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    T0 = $[1];
    basePath = $[2];
    brands = $[3];
    category = $[4];
    factions = $[5];
    piecesMax = $[6];
    piecesMin = $[7];
    scales = $[8];
    searchQuery = $[9];
    subcategory = $[10];
    t0 = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[32] !== T0 || $[33] !== basePath || $[34] !== brands || $[35] !== category || $[36] !== factions || $[37] !== piecesMax || $[38] !== piecesMin || $[39] !== scales || $[40] !== searchQuery || $[41] !== subcategory || $[42] !== t0 || $[43] !== t1 || $[44] !== t2 || $[45] !== t3) {
    t5 = <T0 locale={t0} pageTitle={t1} pageSubtitle={t2} seoTitle={t3} brands={brands} category={category} subcategory={subcategory} searchQuery={searchQuery} factions={factions} scales={scales} piecesMin={piecesMin} piecesMax={piecesMax} pageBasePath={basePath} />;
    $[32] = T0;
    $[33] = basePath;
    $[34] = brands;
    $[35] = category;
    $[36] = factions;
    $[37] = piecesMax;
    $[38] = piecesMin;
    $[39] = scales;
    $[40] = searchQuery;
    $[41] = subcategory;
    $[42] = t0;
    $[43] = t1;
    $[44] = t2;
    $[45] = t3;
    $[46] = t5;
  } else {
    t5 = $[46];
  }
  return t5;
}
function _temp(f) {
  return isMilitaryFaction(f);
}